import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Stack, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import axiosInstance from '../../axiosInstance';
import GedChat from '../GedChat';
import { themeExpert } from '../../theme/expert';

/** Le libellé d'une valeur qui n'est pas du texte. */
const lisible = (v) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

/**
 * Extraire les données d'un document, les relire, puis les porter au dossier.
 *
 * L'extraction ne s'applique jamais seule : les champs restent modifiables,
 * et ceux que l'on corrige sont signalés jusqu'à l'enregistrement.
 */
function Extraction({ unit, onValide }) {
  const [scenarios, setScenarios] = useState([]);
  const [scenario, setScenario] = useState('');
  const [extrait, setExtrait] = useState(null);
  const [champs, setChamps] = useState({});
  const [corriges, setCorriges] = useState(new Set());
  const [analyse, setAnalyse] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    axiosInstance.get('/ged/scenarios/')
      .then(({ data }) => {
        setScenarios(data);
        if (data.length > 0) setScenario(String(data[0].id));
      })
      .catch(() => setErreur("Les scénarios d'analyse n'ont pas pu être chargés."));
  }, []);

  useEffect(() => {
    if (unit?.extracted_data?.data) {
      setExtrait(unit.extracted_data);
      setChamps({ ...unit.extracted_data.data });
    }
  }, [unit]);

  const analyser = async () => {
    setAnalyse(true);
    setErreur('');
    try {
      const { data } = await axiosInstance.post(
        `/ged/unit/${unit.id}/scenario-analyze/`,
        { scenario_id: parseInt(scenario, 10) });
      setExtrait(data.extracted_data);
      setChamps({ ...data.extracted_data.data });
      setCorriges(new Set());
      setMessage('Analyse terminée. Relisez avant d\'enregistrer.');
    } catch {
      setErreur("L'analyse n'a pas abouti.");
    } finally {
      setAnalyse(false);
    }
  };

  const corriger = (cle, valeur) => {
    setChamps((etat) => ({ ...etat, [cle]: valeur }));
    setCorriges((etat) => new Set(etat).add(cle));
  };

  const enregistrer = async () => {
    setOccupe(true);
    setErreur('');
    try {
      if (corriges.size > 0) {
        const modifies = {};
        corriges.forEach((cle) => { modifies[cle] = champs[cle]; });
        await axiosInstance.patch(`/ged/unit/${unit.id}/extracted-data/`,
                                  { fields: modifies });
      }
      await axiosInstance.post(`/ged/unit/${unit.id}/apply/`);
      setCorriges(new Set());
      setMessage('Données portées au dossier.');
      if (onValide) onValide(unit.id);
    } catch (e) {
      setErreur(e.response?.data?.error
        || "Les données n'ont pas pu être portées au dossier.");
    } finally {
      setOccupe(false);
    }
  };

  const valeur = (cle, v) => {
    const corrige = corriges.has(cle);
    const fond = corrige ? 'warning.light' : 'transparent';

    if (Array.isArray(v)) {
      return (
        <Stack spacing={1} sx={{ bgcolor: fond, borderRadius: 1 }}>
          {v.map((element, i) => (
            <Box key={i} sx={{ p: 1, borderRadius: 1, border: '1px solid',
                               borderColor: 'divider' }}>
              {typeof element === 'object' && element !== null
                ? Object.entries(element).map(([k, sous]) => (
                  <Stack key={k} direction="row" spacing={1}>
                    <Typography variant="caption" color="text.secondary"
                                sx={{ minWidth: 120 }}>{k}</Typography>
                    <Typography variant="body2">{lisible(sous)}</Typography>
                  </Stack>
                ))
                : <Typography variant="body2">{lisible(element)}</Typography>}
            </Box>
          ))}
        </Stack>
      );
    }

    if (typeof v === 'object' && v !== null) {
      return (
        <Box sx={{ p: 1, borderRadius: 1, border: '1px solid',
                   borderColor: 'divider', bgcolor: fond }}>
          {Object.entries(v).map(([k, sous]) => (
            <Stack key={k} direction="row" spacing={1}>
              <Typography variant="caption" color="text.secondary"
                          sx={{ minWidth: 120 }}>{k}</Typography>
              <Typography variant="body2">{lisible(sous)}</Typography>
            </Stack>
          ))}
        </Box>
      );
    }

    return (
      <TextField size="small" value={v ?? ''}
                 onChange={(e) => corriger(cle, e.target.value)}
                 sx={{ bgcolor: fond }} />
    );
  };

  return (
    <Stack spacing={2.5}>
      {erreur && <Alert severity="error"
                        onClose={() => setErreur('')}>{erreur}</Alert>}
      {message && <Alert severity="success"
                         onClose={() => setMessage('')}>{message}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
             alignItems={{ sm: 'flex-start' }}>
        <TextField select label="Scénario d'analyse" value={scenario}
                   onChange={(e) => setScenario(e.target.value)}
                   helperText="Il détermine les champs que l'IA cherchera.">
          {scenarios.map((s) => (
            <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={analyser}
                disabled={analyse || !scenario}
                sx={{ mt: { sm: 1 }, flexShrink: 0 }}>
          {analyse ? 'Analyse en cours…' : 'Analyser'}
        </Button>
      </Stack>

      {!extrait ? (
        <Alert severity="info">
          Choisissez un scénario et lancez l'analyse pour extraire les données
          de ce document.
        </Alert>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '35%' }}>Champ</TableCell>
                  <TableCell>Valeur extraite</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(champs).map(([cle, v]) => (
                  <TableRow key={cle}>
                    <TableCell>
                      <Typography variant="body2"
                                  sx={{ fontFamily: 'monospace' }}>
                        {cle}
                      </Typography>
                      {corriges.has(cle) && (
                        <Chip size="small" color="warning" label="corrigé"
                              sx={{ mt: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>{valeur(cle, v)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" spacing={2} alignItems="center"
                 justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {corriges.size > 0
                ? `${corriges.size} champ${corriges.size > 1 ? 's' : ''} corrigé${corriges.size > 1 ? 's' : ''} — vos corrections partent avec.`
                : "Les valeurs viennent de l'IA : relisez-les avant d'enregistrer."}
            </Typography>
            <Button variant="contained" onClick={enregistrer} disabled={occupe}
                    sx={{ flexShrink: 0 }}>
              {occupe ? 'Enregistrement…' : 'Porter au dossier'}
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
}

/** Les départs proposés au chat, selon la nature du document. */
const DEPARTS = {
  devis: [
    { label: 'Créer les postes de coût',
      prompt: 'Analyse ce devis et retourne-moi la liste des postes de travaux avec leurs montants dans un bloc ```json``` au format [{"title": ..., "reclamer": ..., "tva": ..., "origin": "1", "details": [{"title": ..., "unitValue": ..., "quantity": ..., "unit": ...}]}]' },
    { label: 'Résumer', prompt: 'Fais-moi un résumé clair et concis de ce devis.' },
  ],
  facture: [
    { label: 'Résumer', prompt: 'Fais-moi un résumé de cette facture.' },
    { label: 'Extraire les données',
      prompt: 'Extrais les données importantes de cette facture (montant, date, fournisseur, référence).' },
  ],
  default: [
    { label: 'Résumer', prompt: 'Fais-moi un résumé de ce document.' },
    { label: 'Réponse client',
      prompt: 'Aide-moi à rédiger une réponse professionnelle au client en lien avec ce document.' },
  ],
};

function GedAnalysisPanel({ show, onHide, unit, onUnitValidated }) {
  const [onglet, setOnglet] = useState('extraction');
  const [session, setSession] = useState(null);

  const fermer = () => {
    setSession(null);
    onHide();
  };

  if (!unit) return null;

  return (
    <ThemeProvider theme={themeExpert}>
      <Dialog open={Boolean(show)} onClose={fermer} fullWidth maxWidth="lg">
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center"
                 flexWrap="wrap" useFlexGap>
            <span>{unit.title || 'Document'}</span>
            {unit.type && <Chip size="small" variant="outlined"
                                label={unit.type} />}
          </Stack>
        </DialogTitle>

        <Tabs value={onglet} onChange={(_, v) => setOnglet(v)}
              sx={{ px: 3, borderBottom: '1px solid',
                    borderColor: 'divider' }}>
          <Tab value="extraction" label="Extraction" />
          <Tab value="chat" label="Assistant" />
        </Tabs>

        <DialogContent sx={{ pt: 3 }}>
          <Box hidden={onglet !== 'extraction'}>
            <Extraction unit={unit} onValide={onUnitValidated} />
          </Box>
          <Box hidden={onglet !== 'chat'}>
            {/* Monté seulement à l'ouverture de l'onglet : la session ne
                s'ouvre pas tant que personne ne l'a demandée. */}
            {onglet === 'chat' && (
              <GedChat
                documentUnitId={unit.id}
                sessionId={session}
                onSessionCreated={setSession}
                quickActions={DEPARTS[unit.type] || DEPARTS.default}
                emptyHint="Posez une question sur ce document."
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={fermer}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default GedAnalysisPanel;
